import type { Group, Permission, User, UserRole } from '@cheqroom/api';
import { useMemo } from 'react';

export interface Props {
	workspace: Pick<Group, 'profile' | 'limits'> | null;
	/** `customer` is the session contact: an id, or the populated contact (web hydrates `users.me`). */
	user: (Partial<Pick<User, 'customer'>> & { userRole?: Pick<UserRole, 'permissions'> | null }) | null;
}

export const createPermissionHandler = ({ workspace, user }: Props) => {
	const sessionCustomerId = typeof user?.customer === 'string' ? user.customer : user?.customer?._id;
	const { profile, limits } = workspace ?? {};
	const permissions = user?.userRole?.permissions ?? [];

	const features = {
		useGeo: limits?.allowGeo && profile?.useGeo,
		useImport: limits?.allowImport,
		useExport: limits?.allowExport,
		useCustody: limits?.allowCustody && profile?.useCustody,
		useReservations: limits?.allowReservations && profile?.useReservations,
		useCheckouts: limits?.allowOrders && profile?.useOrders,
		useReleaseAtLocation:
			profile?.custodyCanChangeLocation !== undefined ? profile?.custodyCanChangeLocation : true,
		useKits: limits?.allowKits && profile?.useKits,
		useFlags: limits?.allowFlags && profile?.useFlags,
		useWebhooks: limits?.allowWebhooks,
		useUserSync: limits?.allowUserSync && profile?.useUserSync,
		useDepreciations: limits?.allowDepreciations && profile?.useDepreciations,
		useSpotchecks: limits?.allowSpotcheck && profile?.useSpotcheck,
		useBlockContacts: limits?.allowBlockContacts && profile?.useBlockContacts,
		useCustomRoles: limits?.allowCustomRoles,
		useTickets: limits?.allowTickets && profile?.useTickets,
		usePdf: limits?.allowGeneratePdf,
		// python-api `reserveRepeat`: the plan limit and the workspace setting.
		useRepeatReservations: limits?.allowReservationsRepeat && profile?.useReservationsRepeat,
		useReservationsClose: limits?.allowReservations && profile?.useReservations && profile?.useReservationsClose,
		useEquipmentPicking: limits?.allowEquipmentPicking && profile?.useEquipmentPicking,
		orderCheckinLocation: limits?.allowOrders && profile?.useOrders && profile?.orderCheckinLocation,
		usePastReservations: limits?.allowReservations && profile?.useReservations && profile?.usePastReservations,
		useBlockEditOfOpenReservations:
			limits?.allowReservations && profile?.useReservations && profile?.useBlockEditOfOpenReservations,
		usePastOrders: limits?.allowOrders && profile?.useOrders && profile?.usePastOrders,
		useBlockEditOfOpenOrders: limits?.allowOrders && profile?.useOrders && profile?.useBlockEditOfOpenOrders,
	};

	const methods = {
		hasPermission: (permission: Permission) => permissions.includes(permission),
		hasAnyPermission: (...required: Permission[]) => required.some((p) => permissions.includes(p)),
		hasAllPermissions: (...required: Permission[]) => required.every((p) => permissions.includes(p)),
		isOwn: (customerId: string) => !!customerId && customerId === sessionCustomerId,

		canUpgrade: () => methods.hasAnyPermission('ACCOUNT_SUBSCRIPTIONS_ADMIN', 'ACCOUNT_BILLING_ADMIN'),
	};

	return {
		...features,
		...methods,
	};
};

export const usePermissions = ({ workspace, user }: Props) => {
	return useMemo(() => createPermissionHandler({ workspace, user }), [workspace, user]);
};
